import { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
} from "recharts";
import { format, formatDistanceToNow, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import {
  Building2,
  Calendar,
  CalendarClock,
  Loader2,
  Mail,
  MessageCircle,
  PhoneCall,
  Search,
  ArrowUpDown,
  X,
} from "lucide-react";
import Card from "@/components/Card";
import apiClient from "@/lib/api-client";
import OrganizationHealthPanel from "@/components/OrganizationHealthPanel";

// ── Contrato de GET /admin/health/overview?days= (backend ya desplegado, no
// tocar). Ojo: la clave top-level de la respuesta es "overview", no "data".
interface HealthTotals {
  organizations: number;
  activeOrganizations: number;
  reservations: number;
  upcoming: number;
  voiceCalls: number;
  voiceReservations: number;
  emailsSent: number;
  whatsappSent: number;
}

interface HealthTimeSeriesPoint {
  date: string;
  total: number;
  bySource: Record<string, number>;
}

interface OrgSummary {
  id: string;
  name: string;
  reservations: number;
  upcoming: number;
  lastActivityAt: string | null;
  voiceSuccessRatePct: number | null;
}

interface HealthOverview {
  generatedAt: string;
  rangeDays: number;
  totals: HealthTotals;
  reservationsBySource: Record<string, number>;
  reservationsByStatus: Record<string, number>;
  timeSeries: HealthTimeSeriesPoint[];
  organizations: OrgSummary[];
}

interface HealthOverviewResponse {
  success: boolean;
  overview: HealthOverview;
}

type RangeDays = 7 | 30 | 90;

// ── Paleta categórica validada (orden fijo, colores claros). Se asigna color
// POR ENTIDAD (canal/estado) según su posición en el orden fijo, nunca por
// ranking — así el mismo canal/estado siempre tiene el mismo color en todas
// las gráficas de la página.
const CATEGORY_COLORS = [
  "#2a78d6",
  "#008300",
  "#e87ba4",
  "#eda100",
  "#1baf7a",
  "#eb6834",
  "#4a3aa7",
  "#e34948",
];
const NEUTRAL_COLOR = "#898781"; // "Otros" / clave desconocida

const AXIS_LINE_COLOR = "#c3c2b7";
const GRID_COLOR = "#e1e0d9";
const AXIS_TEXT_COLOR = "#898781";
const LABEL_TEXT_COLOR = "#0b0b0b";
const LEGEND_TEXT_COLOR = "#52514e";

const SOURCE_ORDER = [
  "widget",
  "dashboard",
  "phone",
  "phone-ai",
  "web",
  "google",
  "mobile_app",
  "walk-in",
  "whatsapp-ai",
  "instagram-ai",
  "messenger-ai",
];

const STATUS_ORDER = [
  "confirmed",
  "double_confirmed",
  "pending",
  "arrived",
  "seated",
  "completed",
  "cancelled",
  "no_show",
];

const SOURCE_LABELS: Record<string, string> = {
  widget: "Widget",
  dashboard: "Dashboard",
  phone: "Teléfono",
  "phone-ai": "Carmen (voz)",
  web: "Web",
  google: "Google",
  mobile_app: "App móvil",
  "walk-in": "Sin reserva",
  "whatsapp-ai": "WhatsApp IA",
  "instagram-ai": "Instagram IA",
  "messenger-ai": "Messenger IA",
};

const STATUS_LABELS: Record<string, string> = {
  confirmed: "Confirmada",
  double_confirmed: "Doble confirmada",
  pending: "Pendiente",
  arrived: "Llegó",
  seated: "Sentada",
  completed: "Completada",
  cancelled: "Cancelada",
  no_show: "No-show",
};

function labelFor(map: Record<string, string>, rawKey: string): string {
  if (map[rawKey]) return map[rawKey];
  const normalized = rawKey.replace(/[-_]/g, " ");
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function colorForOrder(order: string[], key: string): string {
  const idx = order.indexOf(key);
  if (idx === -1) return NEUTRAL_COLOR;
  return CATEGORY_COLORS[idx % CATEGORY_COLORS.length];
}

const sourceColor = (key: string) => colorForOrder(SOURCE_ORDER, key);
const statusColor = (key: string) => colorForOrder(STATUS_ORDER, key);

function extractErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === "object" && "response" in err) {
    const response = (
      err as { response?: { data?: { error?: string; message?: string } } }
    ).response;
    return response?.data?.error || response?.data?.message || fallback;
  }
  return fallback;
}

function formatDayLabel(dateStr: string): string {
  try {
    return format(parseISO(dateStr), "d MMM", { locale: es });
  } catch {
    return dateStr;
  }
}

function formatLastActivity(dateString: string | null): string {
  if (!dateString) return "—";
  try {
    return formatDistanceToNow(new Date(dateString), {
      addSuffix: true,
      locale: es,
    });
  } catch {
    return "—";
  }
}

// ── Tooltip de la gráfica apilada "Reservas por día": muestra cada canal +
// el total del día.
function StackedTooltip({
  active,
  payload,
  label,
}: Partial<TooltipContentProps<number, string>>) {
  if (!active || !payload || payload.length === 0) return null;
  const total = payload.reduce(
    (acc, item) => acc + (typeof item.value === "number" ? item.value : 0),
    0,
  );
  return (
    <div className="min-w-[170px] rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs shadow-lg">
      <p className="mb-1 font-semibold text-gray-900">
        {formatDayLabel(String(label))}
      </p>
      <div className="space-y-0.5">
        {payload.map((item) => (
          <div
            key={String(item.dataKey)}
            className="flex items-center justify-between gap-4"
          >
            <span className="flex items-center gap-1.5 text-gray-600">
              <span
                className="h-2 w-2 flex-shrink-0 rounded-full"
                style={{ background: item.color }}
              />
              {item.name}
            </span>
            <span className="font-medium text-gray-900">{item.value}</span>
          </div>
        ))}
      </div>
      <div className="mt-1 flex items-center justify-between gap-4 border-t border-gray-100 pt-1 font-semibold text-gray-900">
        <span>Total</span>
        <span>{total}</span>
      </div>
    </div>
  );
}

// ── Tooltip para las gráficas de barra horizontal de 1 serie (canal/estado/
// top clientes): lee nombre/valor/color del propio dato, no del `fill` de la
// serie (que con <Cell> por barra no siempre coincide en el tooltip por defecto).
function SingleBarTooltip({
  active,
  payload,
}: Partial<TooltipContentProps<number, string>>) {
  if (!active || !payload || payload.length === 0) return null;
  const datum = payload[0].payload as {
    name: string;
    value: number;
    color?: string;
  };
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs shadow-lg">
      <div className="flex items-center gap-1.5">
        {datum.color && (
          <span
            className="h-2 w-2 flex-shrink-0 rounded-full"
            style={{ background: datum.color }}
          />
        )}
        <span className="font-medium text-gray-900">{datum.name}</span>
      </div>
      <p className="mt-0.5 text-gray-600">
        Reservas: <span className="font-semibold text-gray-900">{datum.value}</span>
      </p>
    </div>
  );
}

interface HorizontalBarDatum {
  key: string;
  name: string;
  value: number;
  color: string;
}

function HorizontalBarChartCard({
  title,
  data,
  singleColor,
  minHeight = 220,
}: {
  title: string;
  data: HorizontalBarDatum[];
  singleColor?: string;
  minHeight?: number;
}) {
  const chartHeight = Math.max(minHeight, data.length * 34);
  return (
    <Card title={title}>
      {data.length === 0 ? (
        <p className="py-10 text-center text-sm text-gray-400">
          Sin datos en este rango.
        </p>
      ) : (
        <div style={{ height: chartHeight }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              layout="vertical"
              margin={{ top: 4, right: 32, bottom: 4, left: 4 }}
            >
              <CartesianGrid horizontal={false} stroke={GRID_COLOR} />
              <XAxis
                type="number"
                allowDecimals={false}
                tick={{ fill: AXIS_TEXT_COLOR, fontSize: 11 }}
                axisLine={{ stroke: AXIS_LINE_COLOR }}
                tickLine={{ stroke: AXIS_LINE_COLOR }}
              />
              <YAxis
                type="category"
                dataKey="name"
                width={130}
                tick={{ fill: LEGEND_TEXT_COLOR, fontSize: 12 }}
                axisLine={{ stroke: AXIS_LINE_COLOR }}
                tickLine={false}
              />
              <Tooltip
                content={<SingleBarTooltip />}
                cursor={{ fill: "#f5f4f0" }}
              />
              <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={22}>
                {data.map((entry) => (
                  <Cell key={entry.key} fill={singleColor ?? entry.color} />
                ))}
                <LabelList
                  dataKey="value"
                  position="right"
                  style={{ fill: LABEL_TEXT_COLOR, fontSize: 12, fontWeight: 600 }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}

interface KpiCardProps {
  label: string;
  value: number;
  subtext: string;
  icon: typeof Calendar;
  note?: string;
}

function KpiCard({ label, value, subtext, icon: Icon, note }: KpiCardProps) {
  return (
    <Card>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-600">{label}</p>
          <p className="mt-2 text-2xl md:text-3xl font-bold text-gray-900">
            {value.toLocaleString("es-ES")}
          </p>
          <p className="mt-1 text-xs text-gray-500">
            {subtext}
            {note && <span className="ml-1 text-amber-600">{note}</span>}
          </p>
        </div>
        <div className="flex-shrink-0 rounded-lg bg-primary-50 p-2.5 text-primary-600">
          <Icon size={20} />
        </div>
      </div>
    </Card>
  );
}

export default function ClientsHealth() {
  const [rangeDays, setRangeDays] = useState<RangeDays>(30);
  const [overview, setOverview] = useState<HealthOverview | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [selectedOrgName, setSelectedOrgName] = useState("");

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError("");

    apiClient
      .get<HealthOverviewResponse>(`/admin/health/overview?days=${rangeDays}`)
      .then((res) => {
        if (cancelled) return;
        if (res.data?.overview) {
          setOverview(res.data.overview);
        } else {
          setError("Respuesta inesperada del servidor.");
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(
          extractErrorMessage(
            err,
            "No se pudo cargar la salud de la plataforma.",
          ),
        );
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [rangeDays]);

  // Unión de canales presentes en el rango (orden fijo primero, extras al final)
  const sourceKeys = useMemo(() => {
    if (!overview) return [] as string[];
    const set = new Set<string>();
    Object.keys(overview.reservationsBySource).forEach((k) => set.add(k));
    overview.timeSeries.forEach((point) =>
      Object.keys(point.bySource).forEach((k) => set.add(k)),
    );
    const ordered = SOURCE_ORDER.filter((k) => set.has(k));
    const rest = Array.from(set)
      .filter((k) => !SOURCE_ORDER.includes(k))
      .sort();
    return [...ordered, ...rest];
  }, [overview]);

  const dailyChartData = useMemo(() => {
    if (!overview) return [];
    return overview.timeSeries.map((point) => {
      const row: Record<string, number | string> = { date: point.date };
      sourceKeys.forEach((key) => {
        row[key] = point.bySource[key] ?? 0;
      });
      return row;
    });
  }, [overview, sourceKeys]);

  const channelChartData = useMemo((): HorizontalBarDatum[] => {
    if (!overview) return [];
    return Object.entries(overview.reservationsBySource)
      .map(([key, value]) => ({
        key,
        name: labelFor(SOURCE_LABELS, key),
        value,
        color: sourceColor(key),
      }))
      .sort((a, b) => b.value - a.value);
  }, [overview]);

  const statusChartData = useMemo((): HorizontalBarDatum[] => {
    if (!overview) return [];
    const entries = overview.reservationsByStatus;
    const present = new Set(Object.keys(entries));
    const ordered = STATUS_ORDER.filter((k) => present.has(k));
    const rest = Array.from(present)
      .filter((k) => !STATUS_ORDER.includes(k))
      .sort();
    return [...ordered, ...rest].map((key) => ({
      key,
      name: labelFor(STATUS_LABELS, key),
      value: entries[key] ?? 0,
      color: statusColor(key),
    }));
  }, [overview]);

  const topClientsData = useMemo((): HorizontalBarDatum[] => {
    if (!overview) return [];
    return [...overview.organizations]
      .sort((a, b) => b.reservations - a.reservations)
      .slice(0, 12)
      .map((org) => ({
        key: org.id,
        name: org.name,
        value: org.reservations,
        color: "#2a78d6",
      }));
  }, [overview]);

  const filteredOrganizations = useMemo(() => {
    if (!overview) return [];
    const term = search.trim().toLowerCase();
    const filtered = term
      ? overview.organizations.filter((org) =>
          org.name.toLowerCase().includes(term),
        )
      : overview.organizations;
    return [...filtered].sort((a, b) =>
      sortDir === "desc"
        ? b.reservations - a.reservations
        : a.reservations - b.reservations,
    );
  }, [overview, search, sortDir]);

  const kpis: KpiCardProps[] = overview
    ? [
        {
          label: "Reservas",
          value: overview.totals.reservations,
          subtext: `Últimos ${rangeDays} días`,
          icon: Calendar,
        },
        {
          label: "Clientes activos",
          value: overview.totals.activeOrganizations,
          subtext: `${overview.totals.organizations} organizaciones totales`,
          icon: Building2,
        },
        {
          label: "Próximas",
          value: overview.totals.upcoming,
          subtext: "Reservas futuras",
          icon: CalendarClock,
        },
        {
          label: "Llamadas Carmen",
          value: overview.totals.voiceCalls,
          subtext: `${overview.totals.voiceReservations} → reserva`,
          icon: PhoneCall,
        },
        {
          label: "Emails enviados",
          value: overview.totals.emailsSent,
          subtext: `Últimos ${rangeDays} días`,
          icon: Mail,
        },
        {
          label: "WhatsApp enviados",
          value: overview.totals.whatsappSent,
          subtext: "Últimos días",
          icon: MessageCircle,
          note: "* cobertura parcial",
        },
      ]
    : [];

  const handleSelectOrg = (org: OrgSummary) => {
    setSelectedOrgId(org.id);
    setSelectedOrgName(org.name);
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">
            Salud de clientes
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Vista global de la plataforma, con acceso al detalle de cada cliente.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {([7, 30, 90] as RangeDays[]).map((days) => (
            <button
              key={days}
              type="button"
              onClick={() => setRangeDays(days)}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                rangeDays === days
                  ? "bg-primary-600 text-white"
                  : "border border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              {days} días
            </button>
          ))}
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        </div>
      )}

      {!isLoading && error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {!isLoading && !error && overview && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            {kpis.map((kpi) => (
              <KpiCard key={kpi.label} {...kpi} />
            ))}
          </div>

          {/* Reservas por día (stacked) */}
          <Card title="Reservas por día">
            {dailyChartData.length === 0 ? (
              <p className="py-10 text-center text-sm text-gray-400">
                Sin datos en este rango.
              </p>
            ) : (
              <div style={{ height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={dailyChartData}
                    margin={{ top: 8, right: 12, bottom: 4, left: 4 }}
                  >
                    <CartesianGrid vertical={false} stroke={GRID_COLOR} />
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatDayLabel}
                      tick={{ fill: AXIS_TEXT_COLOR, fontSize: 11 }}
                      axisLine={{ stroke: AXIS_LINE_COLOR }}
                      tickLine={{ stroke: AXIS_LINE_COLOR }}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fill: AXIS_TEXT_COLOR, fontSize: 11 }}
                      axisLine={{ stroke: AXIS_LINE_COLOR }}
                      tickLine={{ stroke: AXIS_LINE_COLOR }}
                    />
                    <Tooltip
                      content={<StackedTooltip />}
                      cursor={{ fill: "#f5f4f0" }}
                    />
                    <Legend
                      wrapperStyle={{ fontSize: 12, color: LEGEND_TEXT_COLOR }}
                      formatter={(value: string) => labelFor(SOURCE_LABELS, value)}
                    />
                    {sourceKeys.map((key) => (
                      <Bar
                        key={key}
                        dataKey={key}
                        stackId="reservations"
                        name={labelFor(SOURCE_LABELS, key)}
                        fill={sourceColor(key)}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>

          {/* Por canal / Por estado */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <HorizontalBarChartCard
              title="Reservas por canal"
              data={channelChartData}
            />
            <HorizontalBarChartCard
              title="Reservas por estado"
              data={statusChartData}
            />
          </div>

          {/* Top clientes */}
          <HorizontalBarChartCard
            title="Top clientes por reservas"
            data={topClientsData}
            singleColor="#2a78d6"
            minHeight={260}
          />

          {/* Tabla de clientes */}
          <Card title="Clientes">
            <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="relative w-full sm:max-w-xs">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar cliente por nombre..."
                  className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
              <p className="text-xs text-gray-400">
                {filteredOrganizations.length} de {overview.organizations.length}{" "}
                clientes
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-400">
                    <th className="px-3 py-2 font-medium">Cliente</th>
                    <th className="px-3 py-2 font-medium">
                      <button
                        type="button"
                        onClick={() =>
                          setSortDir((d) => (d === "desc" ? "asc" : "desc"))
                        }
                        className="inline-flex items-center gap-1 hover:text-gray-600"
                      >
                        Reservas
                        <ArrowUpDown className="h-3 w-3" />
                      </button>
                    </th>
                    <th className="px-3 py-2 font-medium">Próximas</th>
                    <th className="px-3 py-2 font-medium">Última actividad</th>
                    <th className="px-3 py-2 font-medium">Tasa voz</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrganizations.map((org) => (
                    <tr
                      key={org.id}
                      onClick={() => handleSelectOrg(org)}
                      className={`cursor-pointer border-b border-gray-100 transition-colors hover:bg-gray-50 ${
                        selectedOrgId === org.id ? "bg-primary-50" : ""
                      }`}
                    >
                      <td className="px-3 py-2 font-medium text-gray-900">
                        {org.name}
                      </td>
                      <td className="px-3 py-2 text-gray-700">
                        {org.reservations}
                      </td>
                      <td className="px-3 py-2 text-gray-700">{org.upcoming}</td>
                      <td className="px-3 py-2 text-gray-500">
                        {formatLastActivity(org.lastActivityAt)}
                      </td>
                      <td className="px-3 py-2 text-gray-700">
                        {org.voiceSuccessRatePct !== null
                          ? `${org.voiceSuccessRatePct}%`
                          : "—"}
                      </td>
                    </tr>
                  ))}
                  {filteredOrganizations.length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-3 py-6 text-center text-gray-400"
                      >
                        No se encontraron clientes.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Drill-down: detalle del cliente seleccionado */}
          {selectedOrgId && (
            <Card>
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-base font-semibold text-gray-900">
                  Detalle de {selectedOrgName}
                </h3>
                <button
                  type="button"
                  onClick={() => setSelectedOrgId(null)}
                  className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
                >
                  <X className="h-4 w-4" />
                  Cerrar
                </button>
              </div>
              <OrganizationHealthPanel organizationId={selectedOrgId} />
            </Card>
          )}
        </>
      )}
    </div>
  );
}
